import { WorkflowSelectService, type FreeLayoutPluginContext, type WorkflowNodeJSON } from '@flowgram.ai/free-layout-editor';
import { nanoid } from 'nanoid';
import { message } from '$/components/Message';
import { useLanguageStore } from '$/store/languageStore';
import { getTeamCanvasLockTabId } from '$/utils/teamCanvasLockTabId';
import type { StoreApi } from 'zustand';
import useFlowNextStore from '$/store/useTeamFlowStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { createMyIpCanvasSync } from '$/pages/RoboneoTeamFlow/utils/myIpCanvasSync';
import { setMyIpCanvasSaveStatus } from '$/pages/RoboneoTeamFlow/utils/myIpCanvasSaveState';
import {
  applyMyIpToCanvas, isMyIpCanvasLimitError, normalizeIpId, removeMyIpFromCanvas, saveMyIpCanvasSnapshot, getMyIpDetail,
} from '$/domains/myIp/myIpService';
import type { MyIpItem } from '$/domains/myIp/types';
import { getMyIpItemMediaUrls } from '$/domains/myIp/tracking';
import { getRegisteredTeamFlowContext } from './teamFlowContextRegistry';
import { isTeamFlowCanvasReadonly } from './isTeamFlowCanvasReadonly';
import {
  buildIpGroupSnapshot,
  getIpGroupSnapshotInitialSize,
  hydrateIpGroupSnapshotImageSizes,
  type IpGroupRoleLabels,
} from './ipGroupSnapshot';
import { isIpGroupNode, getIpGroupInitialSize, getIpGroupPosition } from './ipGroup';
import { getNodeCanvasBounds } from './canvasGridPosition';
import { getViewportCenterCanvasPosition } from './teamFlowClipper/teamFlowClipperViewport';
import { focusNodeViewport } from './focusNodeViewport';
import { getLoadedTeamFlowIdentity } from './teamFlowLoadedIdentity';
import { captureMyIpCanvasContext } from './myIpCanvasContext';
import { roboneoNodeCreate, type NodeSource } from './index';

type Snapshot = ReturnType<FreeLayoutPluginContext['document']['toJSON']>;
const sessions = new WeakMap<FreeLayoutPluginContext, ReturnType<typeof createSession>>();
const flowStore = useFlowNextStore as unknown as StoreApi<{ projectId: string; roomId: string; }>;
const IP_BATCH_ADD_INTERVAL_MS = 500;

/** 画布快照创建时读取当前 Smartcat 词条，避免将中文文案固化在跨语言节点标题中。 */
function getIpGroupRoleLabels(): IpGroupRoleLabels {
  const translate = useLanguageStore.getState().languageStore.smartcatTranslationText;
  return {
    main: translate((texts) => texts.main_image),
    turnaround: translate((texts) => texts.three_views),
    voice: translate((texts) => texts.robo_voice),
    settings: translate((texts) => texts.robo_basic_settings),
    material: translate((texts) => texts.robo_ip_more_assets),
  };
}

export interface AddMyIpToCanvasOptions {
  nodeSource?: Extract<NodeSource, 'save_ip_role' | 'ip_list_click' | 'asset_ip_click'>;
  position?: { x: number; y: number; };
  focus?: boolean;
  /** 批量添加预读的主档，避免第二次请求并确保位置按真实媒体比例计算。 */
  detail?: MyIpItem;
  /** 批量添加预构建的快照，复用已预读的图片自然尺寸。 */
  snapshot?: WorkflowNodeJSON;
}

/** 同步原生 IP 群组，子媒体不重复登记 IP 关系。 */
export function getMyIpCanvasReferences(snapshot: Snapshot) {
  const references: Array<{ nodeId: string; ipId: string; }> = [];
  /** IP 组被普通群组包裹后仍保留同一条 IP 引用关系。 */
  const visit = (nodes: Snapshot['nodes']) => {
    nodes.forEach((node) => {
      if ((node.type === 'group' && node.data?.groupMode === 'ip') && !node.data?.isPre) {
        references.push({ nodeId: node.id, ipId: normalizeIpId(node.data?.ip_id) });
      }
      if (node.blocks) visit(node.blocks);
    });
  };
  visit(snapshot.nodes);
  return references;
}

/** 按 IP 维护当前画布的关系及幂等键，所有异步阶段均验证原上下文仍可写。 */
function createSession(ctx: FreeLayoutPluginContext) {
  const { projectId, roomId } = flowStore.getState();
  const canvasId = getLoadedTeamFlowIdentity(ctx)?.canvasId;
  const { userInfo, clientOId, clientId } = useOfficialStore.getState();
  const uid = userInfo?.uid;
  const matchesIdentity = captureMyIpCanvasContext();
  /** 不允许旧 session 在相同 context 对象被宿主重用后继续写。 */
  const isCurrent = () => {
    const flow = flowStore.getState();
    const official = useOfficialStore.getState();
    return Boolean(matchesIdentity() && projectId && roomId && canvasId && uid && getRegisteredTeamFlowContext() === ctx
      && flow.projectId === projectId && flow.roomId === roomId
      && official.userInfo?.uid === uid && official.clientOId === clientOId && official.clientId === clientId
      && !isTeamFlowCanvasReadonly() && !ctx.playground.config.readonly);
  };
  const initial = getMyIpCanvasReferences(ctx.document.toJSON());
  const generations = new Map<string, string>();
  const sync = createMyIpCanvasSync<Snapshot>({
    initial,
    isCurrent,
    save: (snapshot) => saveMyIpCanvasSnapshot(projectId, canvasId!, JSON.stringify(snapshot), getTeamCanvasLockTabId()),
    upsert: async (ipId) => {
      const key = generations.get(ipId) ?? nanoid();
      generations.set(ipId, key);
      await applyMyIpToCanvas({ projectId, roomId }, ipId, key);
    },
    remove: async (ipId) => {
      await removeMyIpFromCanvas({ projectId, roomId }, ipId);
      // 撤销恢复属于新一代写入，不能复用删除前已完成的幂等键。
      generations.delete(ipId);
    },
  });
  return {
    sync,
    isCurrent,
    matchesIdentity,
    hasReferences: initial.length > 0,
    pendingNodes: new Map<string, string>(),
    /** 当前画布会话首次添加 IP 时确定，后续 IP 组始终沿该水平基线排列。 */
    ipAddBaselineY: undefined as number | undefined,
    limitNotified: false,
  };
}

/** 在首次编辑前登记服务端加载的引用，保证第一步删除也能同步。 */
export function initializeMyIpCanvas(ctx: FreeLayoutPluginContext): void {
  if (!sessions.get(ctx)?.matchesIdentity()) {
    sessions.set(ctx, createSession(ctx));
    setMyIpCanvasSaveStatus(ctx, 'saved');
  }
}

/** 没有 IP 的画布保持原自动保存路径，已有 IP 被删光时仍完成关系清理。 */
export function needsMyIpCanvasSync(ctx: FreeLayoutPluginContext): boolean {
  return Boolean(sessions.get(ctx)?.hasReferences || getMyIpCanvasReferences(ctx.document.toJSON()).length);
}

/** 原生保存与关系写入共用队列，移动/删除/撤销不会并发覆盖引用状态。 */
export async function saveMyIpCanvas(ctx: FreeLayoutPluginContext): Promise<void> {
  initializeMyIpCanvas(ctx);
  const session = sessions.get(ctx)!;
  const snapshot: Snapshot = JSON.parse(JSON.stringify(ctx.document.toJSON()));
  session.hasReferences = true;
  setMyIpCanvasSaveStatus(ctx, 'pending');
  try {
    await session.sync.save(snapshot, getMyIpCanvasReferences(snapshot));
    session.limitNotified = false;
    if (session.matchesIdentity()) setMyIpCanvasSaveStatus(ctx, 'saved');
  } catch (error) {
    if (session.matchesIdentity() && isMyIpCanvasLimitError(error) && !session.limitNotified) {
      session.limitNotified = true;
      // 直接展示后端为 15626 下发的文案，不在前端新增未同步的多语言 key。
      const responseMessage = (error as { response?: { data?: { message?: unknown; }; }; }).response?.data?.message;
      let limitMessage = error instanceof Error ? error.message : '';
      if (typeof responseMessage === 'string') limitMessage = responseMessage;
      if (limitMessage) message.error(limitMessage);
    }
    if (session.matchesIdentity()) setMyIpCanvasSaveStatus(ctx, 'error');
    throw error;
  }
}

/** 以视口中心为起点寻找不覆盖现有节点的落点；超出当前视口时聚焦新节点。 */
function getMyIpPosition(
  ctx: FreeLayoutPluginContext,
  { width, height }: { width: number; height: number; },
  baselineY?: number,
) {
  const center = getViewportCenterCanvasPosition(ctx);
  if (!center) throw new Error('画布尚未就绪，请稍后重试');
  const bounds = ctx.document.getAllNodes()
    .filter((node) => !node.parent || node.parent.flowNodeType === 'root')
    .map((node) => getNodeCanvasBounds(node))
    .filter((bound): bound is NonNullable<ReturnType<typeof getNodeCanvasBounds>> => Boolean(bound));
  return getIpGroupPosition({ x: center.x, y: baselineY ?? center.y - height / 2 }, { width, height }, bounds);
}

/** 应用失败时保留同一节点供重试；成功后再次主动应用可以新增独立节点。 */
export async function addMyIpToCurrentCanvas(
  item: MyIpItem,
  onlyIfAbsent = false,
  isOriginCurrent: () => boolean = captureMyIpCanvasContext(),
  options: AddMyIpToCanvasOptions = {},
): Promise<void> {
  if (!isOriginCurrent()) throw new Error('目标画布已变化，请重新选择角色IP');
  const ctx = getRegisteredTeamFlowContext();
  if (!ctx) throw new Error('请先打开可编辑画布');
  initializeMyIpCanvas(ctx);
  const session = sessions.get(ctx)!;
  if (!session.isCurrent()) throw new Error('当前画布不可编辑');
  const ipId = normalizeIpId(item.ipId);
  if (onlyIfAbsent) {
    const existing = ctx.document.getAllNodes().find((candidate) => String(candidate.toJSON().data?.ip_id ?? '') === ipId);
    if (existing) {
      if (!isIpGroupNode(existing)) throw new Error('项目已有 IP 节点，但节点格式尚不兼容，请勿重复添加');
      await saveMyIpCanvas(ctx);
      if (!isOriginCurrent() || !session.isCurrent() || !ctx.document.getNode(existing.id)) throw new Error('目标节点已变化');
      if (options.focus !== false) focusNodeViewport(ctx, existing);
      return;
    }
  }
  let detail = options.detail ?? item;
  const labels = getIpGroupRoleLabels();
  let node = ctx.document.getNode(session.pendingNodes.get(ipId) ?? '');
  let createdNode = false;
  if (!node) {
    // 资产库列表可能只有摘要，建组前必须读取完整主档与素材。
    detail = options.detail ?? await getMyIpDetail(ipId);
    if (!isOriginCurrent() || !session.isCurrent()) throw new Error('目标画布已变化，请重新选择角色IP');
    node = ctx.document.getNode(session.pendingNodes.get(ipId) ?? '');
    if (!node) {
      // 首次引用只添加基础设定；更多素材由用户在组详情中明确勾选后再加入画布。
      const snapshot = options.snapshot ?? buildIpGroupSnapshot(detail, { x: 0, y: 0 }, undefined, { includeMaterials: false, labels });
      if (options.position) {
        snapshot.meta!.position = options.position;
      } else {
        const position = getMyIpPosition(
          ctx,
          getIpGroupSnapshotInitialSize(snapshot),
          session.ipAddBaselineY,
        );
        session.ipAddBaselineY ??= position.y;
        snapshot.meta!.position = position;
      }
      node = ctx.document.createWorkflowNode(snapshot);
      createdNode = true;
      session.pendingNodes.set(ipId, node.id);
    }
  }
  await saveMyIpCanvas(ctx);
  if (!isOriginCurrent() || !session.isCurrent() || !ctx.document.getNode(node.id)) throw new Error('目标节点或画布上下文已变化');
  session.pendingNodes.delete(ipId);
  if (options.nodeSource) {
    roboneoNodeCreate(node, {
      node_source: options.nodeSource,
      content_source: 'console_timeline',
      content_type: 'IP_NODE',
      content_urls: getMyIpItemMediaUrls(detail).join(','),
      task_id: '',
      before_node_id: '',
      ip_id: ipId,
    });
  }
  if (createdNode) {
    ctx.get(WorkflowSelectService).selection = [node];
    ctx.playground.node.focus();
  }
  if (options.focus !== false) focusNodeViewport(ctx, node);
}

/** 暂停批量落画布的下一项，避免连续保存和节点渲染互相抢占。 */
function waitForNextIpBatchItem(): Promise<void> {
  return new Promise((resolve) => { window.setTimeout(resolve, IP_BATCH_ADD_INTERVAL_MS); });
}

/** 按 IP ID 读取完整主档，再沿同一水平基线串行落画布，避免并发保存覆盖快照。 */
export async function addMyIpsToCurrentCanvas(
  items: Pick<MyIpItem, 'ipId'>[],
  isOriginCurrent: () => boolean = captureMyIpCanvasContext(),
  options: Pick<AddMyIpToCanvasOptions, 'nodeSource'> = {},
): Promise<void> {
  if (!items.length) return;
  if (!isOriginCurrent()) throw new Error('目标画布已变化，请重新选择角色IP');
  const ctx = getRegisteredTeamFlowContext();
  if (!ctx) throw new Error('请先打开可编辑画布');
  initializeMyIpCanvas(ctx);
  const session = sessions.get(ctx)!;
  // 先读全量主档和媒体尺寸，位置计算不再依赖图片渲染后的异步变宽。
  const details = await Promise.all(items.map((item) => getMyIpDetail(normalizeIpId(item.ipId))));
  if (!isOriginCurrent() || !session.isCurrent()) throw new Error('目标画布已变化，请重新选择角色IP');
  const labels = getIpGroupRoleLabels();
  const snapshots = await Promise.all(details.map((detail) => hydrateIpGroupSnapshotImageSizes(
    buildIpGroupSnapshot(detail, { x: 0, y: 0 }, undefined, { includeMaterials: false, labels }),
  )));
  if (!isOriginCurrent() || !session.isCurrent()) throw new Error('目标画布已变化，请重新选择角色IP');
  const sizes = snapshots.map(getIpGroupSnapshotInitialSize);
  const baseline = getMyIpPosition(ctx, sizes[0] ?? getIpGroupInitialSize(0), session.ipAddBaselineY);
  session.ipAddBaselineY ??= baseline.y;
  let lastAddedNode: ReturnType<FreeLayoutPluginContext['document']['getNode']>;

  for (let index = 0; index < items.length; index += 1) {
    // 每项都基于已挂载组的真实 bounds 寻找位置，不能复用图片加载前的预计算结果。
    const position = getMyIpPosition(ctx, sizes[index] ?? getIpGroupInitialSize(0), session.ipAddBaselineY);
    // 按顺序等待每一次保存，防止快照写入相互覆盖。
    // eslint-disable-next-line no-await-in-loop
    await addMyIpToCurrentCanvas(details[index], true, isOriginCurrent, {
      ...options,
      position,
      focus: false,
      detail: details[index],
      snapshot: snapshots[index],
    });
    lastAddedNode = ctx.document.getAllNodes().find((node) => (
      String(node.toJSON().data?.ip_id ?? '') === normalizeIpId(details[index].ipId)
    ));
    if (index < items.length - 1) {
      // eslint-disable-next-line no-await-in-loop
      await waitForNextIpBatchItem();
    }
  }
  /** 批量入口逐项落画布时避免视口抖动；全部完成后聚焦最后一个成功添加的角色。 */
  if (lastAddedNode && isOriginCurrent() && session.isCurrent()) focusNodeViewport(ctx, lastAddedNode);
}
